import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield, TrendingUp, Users } from "lucide-react";
import logoAceleraiHero from "@/assets/logo-acelerai-hero.png";
// LCP image: served from /public with a stable URL so the HTML preload
// scanner can fetch it before the JS bundle executes (eliminates the
// "resource load delay" reported by PageSpeed).
const screenDashboard = "/lcp/screen-dashboard.jpg";
import screenSimulador from "@/assets/screen-simulador-parceiro.jpg";
import screenCrm from "@/assets/screen-crm-parceiro.jpg";

const screens = [
  { src: screenDashboard, alt: "Painel do Parceiro Aceleraí - Dashboard com métricas, comissões e oportunidades" },
  { src: screenSimulador, alt: "Simulador de Financiamento - Compare taxas entre bancos" },
  { src: screenCrm, alt: "CRM - Acompanhe suas vendas, metas e pipeline" },
];

const Hero = () => {
  const [activeScreen, setActiveScreen] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setActiveScreen((prev) => (prev + 1) % screens.length);
      }, 5000);
      return () => clearInterval(interval);
    }, 8000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <section className="relative min-h-[92vh] pt-20 md:pt-24 flex items-center bg-background overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary/8 via-transparent to-accent/5" />
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-primary/6 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-accent/8 rounded-full blur-[100px]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }} />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10 py-8 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-12 items-center">
          {/* Left - Text */}
          <div className="text-center lg:text-left">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0 }}
              className="text-4xl md:text-5xl xl:text-6xl font-extrabold text-foreground leading-[1.15] mb-5 tracking-tight"
            >
              Seja um{" "}
              <span className="text-primary">Parceiro</span> da{" "}
              <span className="sr-only">Aceleraí</span>
              <span className="flex items-center justify-center lg:justify-start mt-2 pl-2 lg:pl-32" aria-hidden="true">
                <img src={logoAceleraiHero} alt="Aceleraí" className="h-[50px] md:h-[65px] xl:h-[76px] object-contain" width={288} height={76} fetchPriority="high" decoding="async" />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="text-base md:text-lg text-muted-foreground mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed font-light"
            >
              A plataforma <span className="text-foreground font-medium">multibancos</span> que centraliza financiamento, crédito e seguros em um só lugar.
              Conecte seu cliente às principais instituições do país <span className="text-foreground font-medium">sem burocracia</span>, e receba comissão a cada contrato fechado.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 mb-8"
            >
              <a href="#planos">
                <Button size="lg" className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 px-8 h-13 text-base font-medium rounded-xl">
                  Começar Agora
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
              <a href="#como-funciona">
                <Button size="lg" variant="outline" className="h-13 text-base px-8 border-border/60 hover:border-primary/40 hover:bg-primary/5 rounded-xl">
                  Saiba Mais
                </Button>
              </a>
            </motion.div>

            {/* Trust indicators - horizontal row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-5 text-sm text-muted-foreground"
            >
              {[
                { icon: Shield, label: "100% Seguro", color: "primary" },
                { icon: TrendingUp, label: "Comissões Altas", color: "success" },
                { icon: Users, label: "+300 Parceiros", color: "accent" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <item.icon className={`w-4 h-4 text-${item.color}`} />
                  <span className="font-medium text-foreground/80">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - Product showcase with alternating screens */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="hidden lg:block"
            style={{ perspective: '1500px' }}
          >
            <div
              className="relative"
              style={{
                transform: 'rotateY(-4deg) rotateX(1.5deg)',
                transformOrigin: 'center center',
              }}
            >
              {/* Ambient glow */}
              <div className="absolute -inset-6 bg-primary/6 rounded-3xl blur-[50px] -z-10" />

              {/* Animated highlight border */}
              <div className="absolute -inset-[1.5px] rounded-xl bg-gradient-to-br from-primary via-accent to-primary opacity-60 animate-pulse -z-[1]" />
              <div className="absolute -inset-[3px] rounded-xl bg-gradient-to-br from-primary/40 via-transparent to-accent/40 blur-sm -z-[2]" />

              {/* Browser frame */}
              <div className="rounded-xl bg-card/70 backdrop-blur-md border border-primary/30 overflow-hidden shadow-[0_24px_80px_-16px_hsl(var(--primary)/0.25)]">
                {/* Toolbar */}
                <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border/25">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-destructive/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-accent/40" />
                    <div className="w-2.5 h-2.5 rounded-full bg-success/40" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-background/40 rounded-md border border-border/15 text-[11px] text-muted-foreground/70 max-w-[220px] w-full justify-center">
                      <svg className="w-2.5 h-2.5 text-success/70" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">www.hubacelerai.com.br</span>
                    </div>
                  </div>
                  <div className="w-[40px]" />
                </div>

                {/* Alternating screenshots */}
                <div className="relative aspect-[16/9] overflow-hidden bg-background/50">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeScreen}
                      src={screens[activeScreen].src}
                      alt={screens[activeScreen].alt}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      fetchPriority="high"
                      decoding="async"
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    />
                  </AnimatePresence>
                </div>

                {/* Screen indicators */}
                <div className="flex justify-center gap-2 py-2 bg-muted/20">
                  {screens.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveScreen(i)}
                      aria-label={`Ir para tela ${i + 1}`}
                      className="min-h-11 min-w-11 flex items-center justify-center rounded-full"
                    >
                      <span
                        className={`block h-2 rounded-full transition-all duration-300 ${
                          i === activeScreen
                            ? "bg-primary w-6"
                            : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile - Product showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:hidden col-span-1"
          >
            <div className="relative">
              <div className="absolute -inset-[1.5px] rounded-xl bg-gradient-to-br from-primary via-accent to-primary opacity-60 animate-pulse -z-[1]" />
              <div className="absolute -inset-[3px] rounded-xl bg-gradient-to-br from-primary/40 via-transparent to-accent/40 blur-sm -z-[2]" />
            <div className="rounded-xl bg-card/70 border border-primary/30 overflow-hidden shadow-xl shadow-primary/15 relative">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border/25">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-destructive/50" />
                  <div className="w-2 h-2 rounded-full bg-accent/40" />
                  <div className="w-2 h-2 rounded-full bg-success/40" />
                </div>
                <div className="flex-1 flex justify-center">
                  <span className="text-[10px] text-muted-foreground/60 font-medium">www.hubacelerai.com.br</span>
                </div>
              </div>
              <div className="relative aspect-[16/9] overflow-hidden bg-background/50">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeScreen}
                    src={screens[activeScreen].src}
                    alt={screens[activeScreen].alt}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>
              </div>
              {/* Mobile indicators */}
              <div className="flex justify-center gap-2 py-2 bg-muted/20">
                {screens.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveScreen(i)}
                    aria-label={`Ir para tela ${i + 1}`}
                    className="min-h-11 min-w-11 flex items-center justify-center rounded-full"
                  >
                    <span
                      className={`block h-1.5 rounded-full transition-all duration-300 ${
                        i === activeScreen
                          ? "bg-primary w-5"
                          : "w-1.5 bg-muted-foreground/30"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;